import { useEffect, useId } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { WizardFooter } from '~/components/overlays';
import { RetryBanner } from '~/components/ui/RetryBanner';
import type { InitialProductLengthPick } from '~/components/common/productLengthChoice';
import { useGetBuyList } from '~/api/buyList';
import type { RootState } from '~/redux/store';
import { BuyListSummaryTable } from './BuyListSummaryTable';
import { BuyListItemsSection } from './BuyListItemsSection';

export type BuyListItemsStep = 'products' | 'review';

type BuyListStepContentProps = {
  buyListId: string;
  step: 'details' | BuyListItemsStep;
  onSavingChange: (isSaving: boolean) => void;
  onPendingPickChange: (hasPendingPick: boolean) => void;
  initialPick?: InitialProductLengthPick;
};

export function BuyListDetailLoading() {
  const { t } = useTranslation('inventory');
  return (
    <p className="text-sm text-neut-sec" data-testid="buy-list-detail-loading">
      {t('inventory.buy_list.detail_loading')}
    </p>
  );
}

export function BuyListStepContent({
  buyListId,
  step,
  onSavingChange,
  onPendingPickChange,
  initialPick,
}: Readonly<BuyListStepContentProps>) {
  const { t } = useTranslation('inventory');
  const currentUserId = useSelector((state: RootState) =>
    state.auth.user ? state.auth.user.id : null
  );
  const { data, isLoading, isError, refetch } = useGetBuyList(buyListId);
  const hasLoadFailed = !isLoading && (isError || !data);

  useEffect(() => {
    if (hasLoadFailed) onPendingPickChange(false);
  }, [hasLoadFailed, onPendingPickChange]);

  if (isLoading) return <BuyListDetailLoading />;
  if (hasLoadFailed || !data) {
    return (
      <RetryBanner
        message={t('inventory.buy_list.detail_load_failed')}
        retryLabel={t('inventory.retry')}
        onRetry={() => void refetch()}
        testId="buy-list-detail"
      />
    );
  }
  const { buyList } = data;
  const summary = (
    <BuyListSummaryTable
      buyList={buyList}
      testId="buy-list-detail-summary-table"
      showBuyerColumn={false}
    />
  );
  if (step === 'details') return summary;
  return (
    <div className="flex flex-col gap-6">
      {step === 'review' && summary}
      <BuyListItemsSection
        buyListId={buyListId}
        transportVehicleId={buyList.transportVehicle.id}
        items={buyList.items}
        tallies={buyList.tallies}
        canManageItems={step === 'products'}
        viewerId={currentUserId}
        initialPick={initialPick}
        onSavingChange={onSavingChange}
        onPendingPickChange={onPendingPickChange}
      />
    </div>
  );
}

type BuyListItemsStepsFooterProps = {
  step: BuyListItemsStep;
  isSaving: boolean;
  hasPendingPick: boolean;
  testIdPrefix: string;
  onStepChange: (step: BuyListItemsStep) => void;
  onBackFromProducts: () => void;
  onDone: () => void;
};

export function BuyListItemsStepsFooter({
  step,
  isSaving,
  hasPendingPick,
  testIdPrefix,
  onStepChange,
  onBackFromProducts,
  onDone,
}: Readonly<BuyListItemsStepsFooterProps>) {
  const { t } = useTranslation('inventory');
  const pendingPickHintId = useId();

  return (
    <WizardFooter
      start={
        <Button
          variant="secondary"
          width="auto"
          disabled={isSaving}
          onClick={
            step === 'review'
              ? () => onStepChange('products')
              : onBackFromProducts
          }
          data-testid={`${testIdPrefix}-back`}
        >
          {t('inventory.buy_list.back')}
        </Button>
      }
      end={
        step === 'review' ? (
          <Button
            variant="primary"
            width="auto"
            disabled={isSaving}
            onClick={onDone}
            data-testid={`${testIdPrefix}-done`}
          >
            {t('inventory.buy_list.create_done')}
          </Button>
        ) : (
          <>
            {hasPendingPick && (
              <p
                id={pendingPickHintId}
                className="text-sm text-neut-sec"
                data-testid={`${testIdPrefix}-pending-pick-hint`}
              >
                {t('inventory.buy_list.pending_pick_hint')}
              </p>
            )}
            <Button
              variant="primary"
              width="auto"
              disabled={isSaving || hasPendingPick}
              onClick={() => onStepChange('review')}
              aria-describedby={hasPendingPick ? pendingPickHintId : undefined}
              data-testid={`${testIdPrefix}-next`}
            >
              {t('inventory.buy_list.create_next')}
            </Button>
          </>
        )
      }
    />
  );
}
